import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Info } from 'components/Info/Info';
import TextField from '@material-ui/core/TextField';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import { AuthForm } from 'components/AuthForm';
import { Row } from 'components/UI/Row';
import { History } from 'history';
import { AppDispatch } from 'store/store';
import { AccountType, signupFields, signupInfoFields, SignupSchema, SignUpFormProps } from './SignUpSchema';
import { changeAuthPage, signup, getAuthErrors, getErrorFields } from './auth';

const SignUp = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const history: History = useHistory();
  const { register, handleSubmit, errors, watch, control, clearErrors } = useForm<SignUpFormProps>({
    mode: 'onBlur',
    resolver: yupResolver(SignupSchema)
  });
  const selectedAccountType = watch('role');

  const onSubmit = useCallback(
    async (formData: SignUpFormProps) => {
      dispatch(signup(formData, history));
    },
    [dispatch, history]
  );

  const authError = useSelector(getAuthErrors);
  const errorFields = useSelector(getErrorFields);
  useEffect(() => {
    if (authError) {
      clearErrors(errorFields);
    }
  }, [authError, errorFields, clearErrors]);

  return (
    <>
      <AuthForm onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormControl variant="outlined" error={!isEmpty(errors.role)} style={{ marginBottom: '16px' }}>
          <InputLabel id="account-type-label">{t('sign up.account type')}</InputLabel>
          <Controller
            as={
              <Select
                labelId="account-type-label"
                input={<OutlinedInput label={t('sign up.account type')} style={{ width: '400px' }} />}
              >
                <MenuItem value="">{t('sign up.account type choice')}</MenuItem>
                <MenuItem value={AccountType.Common}>{t('sign up.individual')}</MenuItem>
                <MenuItem value={AccountType.Company}>{t('sign up.company')}</MenuItem>
              </Select>
            }
            control={control}
            defaultValue=""
            name="role"
          />

          <FormHelperText>{get(errors, 'role.message')}</FormHelperText>
        </FormControl>

        {selectedAccountType && (
          <>
            {signupFields.map(({ label, name, type }) => (
              <TextField
                key={name}
                autoComplete="new-password"
                helperText={t(get(errors, `${name}.message`))}
                error={!isEmpty(get(errors, `${name}`))}
                label={t(label)}
                inputRef={register}
                name={name}
                type={type}
                style={{ marginBottom: '16px', width: '400px' }}
                variant="outlined"
              />
            ))}
          </>
        )}

        {signupInfoFields.map(({ label, name, role, type }) => (
          <React.Fragment key={name}>
            {selectedAccountType === role && (
              <>
                <TextField
                  key={name}
                  helperText={t(get(errors, `${name}.message`))}
                  error={!isEmpty(get(errors, `${name}.message`))}
                  label={t(label)}
                  inputRef={register}
                  name={name}
                  type={type}
                  style={{ marginBottom: '16px', width: '400px' }}
                  variant="outlined"
                />
              </>
            )}
          </React.Fragment>
        ))}

        {authError && <FormHelperText style={{ color: '#f44336' }}>{authError}</FormHelperText>}

        <Row justifyContent="space-between" width="400px" margin="20px 0 0">
          <Button
            variant="outlined"
            color="secondary"
            style={{ height: '44px', width: '128px' }}
            onClick={() => {
              dispatch(changeAuthPage());
              history.push('/');
            }}
          >
            {t('label.cancel')}
          </Button>
          <Button
            disabled={!selectedAccountType}
            variant="contained"
            color="primary"
            type="submit"
            style={{ height: '44px', width: '128px' }}
          >
            {t('sign up.confirm')}
          </Button>
        </Row>
      </AuthForm>
      <Info />
    </>
  );
};

export { SignUp };
